
import { supabase } from '@/integrations/supabase/client';
import { UserWithRoles, AppRole } from '@/types/supabase';
import { toast } from 'sonner';

/**
 * Fetches all users with their profiles and roles
 */
export const fetchUsersWithRoles = async (): Promise<UserWithRoles[]> => {
  try {
    // Fetch users from auth.users (possible via service role only in a real app)
    // Here we're using a workaround with user_profiles since we don't have service role access
    const { data: profiles, error: profilesError } = await supabase
      .from('user_profiles')
      .select('*');

    if (profilesError) throw profilesError;

    // Fetch all roles
    const { data: allRoles, error: rolesError } = await supabase
      .from('user_roles')
      .select('*');

    if (rolesError) throw rolesError;

    // Get users from auth (this would normally be done with service role)
    // For this demo, we'll reconstruct a partial user object from profiles
    const users: UserWithRoles[] = await Promise.all(
      profiles.map(async (profile) => {
        // Get email from auth if possible (not possible in this demo)
        // Instead, we'll use the ID as a placeholder for email
        const userEmail = `user-${profile.id.substring(0, 8)}@example.com`;
        
        // Get user roles
        const userRoles = allRoles
          .filter(role => role.user_id === profile.id)
          .map(role => role.role);

        // Transform the profile to ensure second_me_config is properly typed
        const typedProfile = {
          ...profile,
          second_me_config: profile.second_me_config ? profile.second_me_config as Record<string, any> : null
        };

        return {
          id: profile.id,
          email: userEmail,
          profile: typedProfile,
          roles: userRoles as AppRole[]
        };
      })
    );

    return users;
  } catch (error) {
    console.error('Error fetching users with roles:', error);
    throw error;
  }
};

/**
 * Adds a role to a user
 */
export const addUserRole = async (userId: string, role: AppRole): Promise<void> => {
  try {
    // Check if user already has this role
    const { data: existingRoles, error: checkError } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId)
      .eq('role', role);

    if (checkError) throw checkError;

    // If user doesn't have this role, add it
    if (existingRoles.length === 0) {
      const { error } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role });

      if (error) throw error;
      
      toast.success(`User has been assigned the ${role} role`);
    } else {
      toast.info(`User already has the ${role} role`);
    }
  } catch (error) {
    console.error('Error adding user role:', error);
    toast.error(`Failed to assign ${role} role`);
    throw error;
  }
};

/**
 * Removes a role from a user
 */
export const removeUserRole = async (userId: string, role: AppRole): Promise<void> => {
  try {
    // Don't allow removing the last role
    const { data: userRoles, error: countError } = await supabase
      .from('user_roles')
      .select('*')
      .eq('user_id', userId);

    if (countError) throw countError;

    if (userRoles.length <= 1) {
      toast.error("Cannot remove the user's only role");
      return;
    }

    // Remove the role
    const { error } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role', role);

    if (error) throw error;
    
    toast.success(`User's ${role} role has been removed`);
  } catch (error) {
    console.error('Error removing user role:', error);
    toast.error(`Failed to remove ${role} role`);
    throw error;
  }
};
